'use client';

import { Share } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { cloneElement, isValidElement, lazy, Suspense, useEffect, useState } from 'react';
import { Button } from '@//components/Button';

const ShareButtonModal = lazy(() => import('./ShareButtonModal'));

interface ShareButtonProps {
  children?: React.ReactNode;
  renderButton?: (onClick: () => void) => React.ReactNode;
}

export const ShareButton: React.FC<ShareButtonProps> = ({ children, renderButton }) => {
  const t = useTranslations('components.shareButton');
  const [showModal, setShowModal] = useState(false);
  const [currentUrl, setCurrentUrl] = useState('');

  useEffect(() => {
    setCurrentUrl(window.location.href);
  }, []);

  const onClick = () => {
    if (navigator.share) {
      navigator.share({
        url: currentUrl,
      });
    } else {
      setShowModal(true);
    }
  };

  const onCloseModal = () => {
    setShowModal(false);
  };

  const renderTrigger = () => {
    if (renderButton) {
      return renderButton(onClick);
    }

    if (children) {
      // If children is a valid React element, clone it and add the onClick
      if (isValidElement(children)) {
        const element = children as React.ReactElement<any>;
        return cloneElement(element, {
          onClick: (e: React.MouseEvent) => {
            if (element.props.onClick) {
              element.props.onClick(e);
            }
            onClick();
          },
        });
      }

      // If it's not a valid element (like text), wrap it in a button
      return (
        <button onClick={onClick} title={t('button.title')} aria-label={t('button.title')}>
          {children}
        </button>
      );
    }

    return (
      <Button
        onClick={onClick}
        title={t('button.title')}
        aria-label={t('button.title')}
        sizeLtMd="md"
        sizeMd="lg"
        variant="secondary"
      >
        <Share aria-hidden="true" />
        {t('button.label')}
      </Button>
    );
  };

  return (
    <>
      {renderTrigger()}
      <Suspense fallback={null}>
        <ShareButtonModal isOpen={showModal} currentUrl={currentUrl} onClose={onCloseModal} />
      </Suspense>
    </>
  );
};
